'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CountryCode } from '@sola/schema/dotcom/shared/geo.js';

/* eslint-disable no-param-reassign */
export const postalCodePatternUS = /^\d{5}((-|(\s*))?\d{4})?$/;
export const postalCodeMaskMX = /^\d{5}$/;
export const postalCodePatternCanada = /^[a-zA-Z]\d[a-zA-Z] ?\d[a-zA-Z]\d$/;

const datePatterns = {
  usAndEs: /^\d{2}\/\d{2}\/\d{4}$/,
  ko: /^\d{4}\.\d{2}\.\d{2}$/,
};

export type GeneratePostalPatternFunc = (countryCode: CountryCode) => {
  value: RegExp;
  message: string;
} | null;

/**
 * Hook for generating postal code patterns based on country ISO code.
 */
export const usePostalPattern = (countryCode: CountryCode) => {
  const { t } = useTranslation(['forms']);
  const [generatePattern, setGeneratePattern] =
    useState<GeneratePostalPatternFunc | null>(null);

  /**
   * Function to lazy load the pattern assets.
   * This will only be called once, and serves to load pattern dependencies that are needed
   * for the generatePattern function, which can then be subsequently used to dynamically generate
   * a patterns, placeholders, and validations based on country code.
   *
   * @TODO Don't forget to check how these imports are cached to make sure browser isn't downloading a unique bundle every time.
   *
   */
  const loadPattern = useCallback(async () => {
    const generatePatternFunc = (countryCodeToPattern: CountryCode) => {
      switch (countryCodeToPattern) {
        case 'US':
          return {
            value: postalCodePatternUS,
            message: t('forms:errors.invalidZipCode'),
          };
        case 'MX':
          return {
            value: postalCodeMaskMX,
            message: t('forms:errors.invalidPostalCodeMexico'),
          };
        case 'CA':
          return {
            value: postalCodePatternCanada,
            message: t('forms:errors.invalidPostalCodeCanada'),
          };
        default:
          return null;
      }
    };

    setGeneratePattern(() => generatePatternFunc);
  }, []);

  /**
   * Generate a pattern anytime the country code changes.
   */
  const pattern = useMemo(() => {
    if (!generatePattern) {
      return;
    }
    return generatePattern(countryCode);
  }, [countryCode, generatePattern]);

  return {
    loadPattern,
    pattern,
  };
};

type GenerateDatePatternFunc = (localeCode: string) => RegExp | null;

/**
 * Hook for generating date patterns based on locale.
 */
export const useDatePattern = (localeCode: string) => {
  const { t } = useTranslation();
  const [generatePattern, setGeneratePattern] =
    useState<GenerateDatePatternFunc | null>(null);

  /**
   * Function to lazy load the pattern assets.
   * This will only be called once, and serves to load pattern dependencies that are needed
   * for the generatePattern function, which can then be subsequently used to dynamically generate
   * a patterns, placeholders, and validations based on country code.
   *
   * @TODO Don't forget to check how these imports are cached to make sure browser isn't downloading a unique bundle every time.
   *
   */
  const loadPattern = useCallback(async () => {
    const generatePatternFunc = (localeCodeToPattern: string) => {
      switch (localeCodeToPattern) {
        case 'ko':
          return datePatterns.ko;
        default:
          return datePatterns.usAndEs;
      }
    };

    setGeneratePattern(() => generatePatternFunc);
  }, []);

  /**
   * Generate a pattern anytime the country code changes.
   */
  const pattern = useMemo(() => {
    if (!generatePattern) {
      return;
    }
    return generatePattern(localeCode);
  }, [localeCode, generatePattern]);

  return {
    loadPattern,
    pattern,
  };
};

export const removeIllegalChars = (
  event: React.ChangeEvent<HTMLInputElement>,
  regex: RegExp,
) => {
  const matches = (event.currentTarget.value.match(regex) || []).length;
  if (matches > 0) {
    let start = 0;
    let end = 0;
    if (event.currentTarget.type !== 'number') {
      start = event.currentTarget.selectionStart
        ? event.currentTarget.selectionStart - 2 * matches
        : event.currentTarget.value.length;
      end = event.currentTarget.selectionEnd
        ? event.currentTarget.selectionEnd - 2 * matches
        : event.currentTarget.value.length;
    }
    event.currentTarget.value = event.currentTarget.value.replace(regex, '');
    if (event.currentTarget.type !== 'number') {
      // Reset cursor position
      event.currentTarget.selectionStart = start;
      event.currentTarget.selectionEnd = end;
    }
  }
};
